import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import MonacoEditor from "react-monaco-editor";
import {
  LuArrowLeft,
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuCircleHelp,
  LuCode,
  LuEraser,
  LuFileText,
  LuLightbulb,
  LuLogOut,
  LuMic,
  LuMicOff,
  LuPlay,
  LuSave,
  LuStar,
  LuTerminal,
  LuX,
} from "react-icons/lu";
import { Btn, Spinner, Note, Dialog } from "../components/Ui";
import { setAnswers, setQuestionAnswer, setDocId } from "../../store/actions/actions";
import "./InterviewExam.css";

/* Endpoints — identical to the legacy authenticated InterviewExam.jsx */
const CODE_API = "https://us-east1-foursssolutions.cloudfunctions.net/interview_prep_code_submission_v2";
const IP_API = "https://us-east1-foursssolutions.cloudfunctions.net/interview_prep_function_v2";

/* Mounted as a BARE authenticated route (no AppShell chrome) — the page wraps
   itself in .csx/.csx-page and owns the viewport. */
export default function InterviewExam() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { questionAnswer, docId, answers, apiToken, scenarioBased, interviewLevel } = useSelector(
    (state) => state.profile
  );
  const userEmail = useSelector((state) => state.auth.auth.email);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [Output, setOutput] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [voutput, SetVoutput] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [examResultsPopup, setShowResultsPopup] = useState(false);
  const [resultQuestion, setResultQuestion] = useState([]);
  const [voiceAssistEnabled] = useState(true);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [notification, setNotification] = useState("");

  /* Where this exam was launched from, so labels/back-nav match the section the
     user came from (Interview prep vs Aspire Quest) — Pavithra Interview Prep #3. */
  const examSource =
    (typeof localStorage !== "undefined" && localStorage.getItem("examSource")) || "aspire-quest";
  const backTo = examSource === "interview-prep" ? "/interview-prep" : "/aspire-quest";
  const backLabel = examSource === "interview-prep" ? "Interview prep" : "Aspire Quest";

  /* ---------- restore exam from localStorage (only for the SAME exam) ---------- */
  useEffect(() => {
    const savedDocId = localStorage.getItem("docId");

    /* A NEW exam arrives with its own docId + questionAnswer already in redux
       (set by the launcher). If that differs from what's saved, this is a fresh
       exam — start with EMPTY answers, don't carry over the previous exam's text
       (Pavithra Interview Prep #2). */
    if (docId && questionAnswer && questionAnswer.length && savedDocId && savedDocId !== docId) {
      const fresh = Array(questionAnswer.length).fill("");
      dispatch(setAnswers(fresh));
      localStorage.setItem("answers", JSON.stringify(fresh));
      localStorage.setItem("docId", docId);
      return;
    }

    const savedAnswers = JSON.parse(localStorage.getItem("answers"));
    const savedQuestionAnswer = JSON.parse(localStorage.getItem("questionAnswer"));
    if (savedQuestionAnswer) {
      dispatch(setQuestionAnswer(savedQuestionAnswer));
      dispatch(setAnswers(savedAnswers || Array(savedQuestionAnswer.length).fill("")));
    }
    if (savedDocId && !docId) dispatch(setDocId(savedDocId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  /* ---------- persist exam state (identical to legacy) ---------- */
  useEffect(() => {
    if (answers) localStorage.setItem("answers", JSON.stringify(answers));
    if (questionAnswer) localStorage.setItem("questionAnswer", JSON.stringify(questionAnswer));
    if (docId) localStorage.setItem("docId", docId);
  }, [answers, questionAnswer, docId]);

  const handleAnswerChange = (event) => {
    const updatedAnswers = [...answers];
    updatedAnswers[currentQuestionIndex] = event.target.value;
    dispatch(setAnswers(updatedAnswers));
  };

  const handleEditorChange = (value) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = value;
    dispatch(setAnswers(newAnswers));
  };

  const clearEditorAnswer = () => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = "";
    dispatch(setAnswers(newAnswers));
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      resetTranscript();
      setOutput(false);
      SetVoutput("");
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < Object.keys(questionAnswer)?.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      resetTranscript();
      setOutput(false);
      SetVoutput("");
    }
  };

  /* ---------- run code (payload/headers identical to legacy validate) ---------- */
  const validate = async () => {
    setShowLoader(true);
    const valPayLoad = {
      code: answers[currentQuestionIndex],
      email: userEmail,
      language: questionAnswer[currentQuestionIndex]?.language,
    };
    try {
      const response = await fetch(CODE_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(valPayLoad),
      });
      if (response.ok) {
        const data = await response.json();
        SetVoutput(data);
        setOutput(true);
        setShowLoader(false);
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      setShowLoader(false);
    }
  };

  const LeaveInterview = () => {
    SaveToDraft();
  };

  /* ---------- save draft (task/payload identical to legacy) ---------- */
  const SaveToDraft = async () => {
    const payLoad = {
      email: userEmail,
      task: "submit_answers",
      questions: questionAnswer,
      answers,
      completed: false,
      scenario_based: scenarioBased,
      level: interviewLevel,
      doc_id: docId,
    };
    setShowLoader(true);
    try {
      const response = await fetch(IP_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(payLoad),
      });
      if (response.ok) {
        await response.json();
        setNotification("Draft saved"); // visible confirmation (Pavithra Interview Prep #5)
        setTimeout(() => setNotification(""), 2500);
        setTimeout(() => {
          setShowLoader(false);
        }, 1000);
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      setShowLoader(false);
    }
  };

  /* ---------- final submission (task/payload identical to legacy) ---------- */
  const SubmitExam = async () => {
    const payLoad = {
      email: userEmail,
      task: "submit_answers",
      questions: questionAnswer,
      answers,
      completed: true,
      scenario_based: scenarioBased,
      level: interviewLevel,
      doc_id: docId,
    };
    setShowLoader(true);
    try {
      const response = await fetch(IP_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(payLoad),
      });
      if (response.ok) {
        const data = await response.json();
        console.log("Exam submitted:", data);
        setShowLoader(false);
        setResultQuestion(data);
        examcomplete();
      }
    } catch (error) {
      console.error("Error submitting exam:", error);
      setShowLoader(false);
    }
  };

  function examcomplete() {
    setShowPopup(true);
  }

  function showResult() {
    setShowPopup(false);
    setShowResultsPopup(true);
  }

  /* ---------- speech recognition (wiring identical to legacy) ---------- */
  const [isListening, setIsListening] = useState(false);
  const { transcript, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const [voiceAssist, setVoiceAssist] = useState(false);

  const toggleListening = () => {
    if (isListening) {
      setVoiceAssist(false);
      setTimeout(() => {
        SpeechRecognition.stopListening();
        setIsListening(false);

        if (transcript) {
          const updatedAnswers = [...answers];
          const prevAns = updatedAnswers[currentQuestionIndex] || "";
          updatedAnswers[currentQuestionIndex] = prevAns ? `${prevAns.replace(/\s+$/, "")} ${transcript}` : transcript;
          dispatch(setAnswers(updatedAnswers));
        }

        resetTranscript();
      }, 1500);
    } else {
      setVoiceAssist(true);
      SpeechRecognition.startListening({ continuous: true, language: "en-US" });
      setIsListening(true);
    }
  };

  /* spacebar toggles recognition (guards + 1500ms flush identical to legacy) */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.target.tagName === "TEXTAREA" || event.target.className.includes("monaco-editor")) {
        return;
      }
      if (!voiceAssistEnabled) return;

      if (event.code === "Space") {
        event.preventDefault();

        if (isListening) {
          setVoiceAssist(false);
          setTimeout(() => {
            SpeechRecognition.stopListening();
            setIsListening(false);

            if (transcript) {
              const updatedAnswers = [...answers];
              const prevAns = updatedAnswers[currentQuestionIndex] || "";
              // separate newly spoken text with a space (Pavithra Interview Prep #5)
              updatedAnswers[currentQuestionIndex] = prevAns ? `${prevAns.replace(/\s+$/, "")} ${transcript}` : transcript;
              dispatch(setAnswers(updatedAnswers));
            }

            resetTranscript();
          }, 1500);
        } else {
          setVoiceAssist(true);
          SpeechRecognition.startListening({ continuous: true, language: "en-US" });
          setIsListening(true);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isListening, transcript, answers, currentQuestionIndex, dispatch, resetTranscript, voiceAssistEnabled]);

  /* ---------- assessment copy/paste restrictions (identical to legacy, but
     surfaced via the in-page notification instead of window.alert) ---------- */
  useEffect(() => {
    const showNotification = (message) => {
      setNotification(message);
      setTimeout(() => setNotification(""), 2000);
    };

    const handleRestrictedAction = (event, message) => {
      event.preventDefault();
      showNotification(message);
      return false;
    };

    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && ["c", "v", "x"].includes(event.key.toLowerCase())) {
        return handleRestrictedAction(event, "Copying and pasting is not allowed in this assessment.");
      }
    };

    const handleContextMenu = (event) => handleRestrictedAction(event, "Right-click is disabled.");
    const handleDragStart = (event) => handleRestrictedAction(event, "Drag and drop is not allowed.");
    const handleDrop = (event) => handleRestrictedAction(event, "Drag and drop is not allowed.");
    const handleCopy = (event) => handleRestrictedAction(event, "Copying and pasting is not allowed in this assessment.");
    const handleCut = (event) => handleRestrictedAction(event, "Copying and pasting is not allowed in this assessment.");
    const handlePaste = (event) => handleRestrictedAction(event, "Copying and pasting is not allowed in this assessment.");

    const textarea = document.getElementById("answerTextarea");
    if (textarea) {
      textarea.addEventListener("keydown", handleKeyDown);
      textarea.addEventListener("contextmenu", handleContextMenu);
      textarea.addEventListener("dragstart", handleDragStart);
      textarea.addEventListener("drop", handleDrop);
      textarea.addEventListener("copy", handleCopy);
      textarea.addEventListener("cut", handleCut);
      textarea.addEventListener("paste", handlePaste);
    }

    return () => {
      if (textarea) {
        textarea.removeEventListener("keydown", handleKeyDown);
        textarea.removeEventListener("contextmenu", handleContextMenu);
        textarea.removeEventListener("dragstart", handleDragStart);
        textarea.removeEventListener("drop", handleDrop);
        textarea.removeEventListener("copy", handleCopy);
        textarea.removeEventListener("cut", handleCut);
        textarea.removeEventListener("paste", handlePaste);
      }
    };
  }, []);

  const notifyRestricted = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(""), 2000);
  };

  const handleCopy = (e) => {
    e.preventDefault();
    notifyRestricted("Copying is disabled!");
  };

  const handlePaste = (e) => {
    e.preventDefault();
    notifyRestricted("Pasting is disabled!");
  };

  const confirmLeave = () => {
    setLeaveOpen(false);
    LeaveInterview();
    navigate(backTo);
  };

  /* ---------- derived values (access patterns identical to legacy) ---------- */
  const total = questionAnswer ? Object.keys(questionAnswer)?.length : 0;
  const isLast = currentQuestionIndex === total - 1;
  const pct = total > 0 ? ((currentQuestionIndex + 1) / total) * 100 : 0;
  const questionText =
    questionAnswer && questionAnswer[currentQuestionIndex] && Object.values(questionAnswer[currentQuestionIndex])[0];
  const isCode = questionAnswer && questionAnswer[currentQuestionIndex]?.initialize_code === "yes";
  const monacoTheme =
    typeof document !== "undefined" && document.documentElement.getAttribute("data-cs-theme") === "light"
      ? "vs"
      : "vs-dark";

  /* ---------- edge state: nothing to run ---------- */
  if (!total) {
    return (
      <div className="csx">
        <div className="csx-page ie-page">
          <main className="ie-main" style={{ justifyContent: "center" }}>
            <div className="ld-empty">
              <span className="ld-empty-icon">
                <LuCircleHelp size={20} />
              </span>
              <div className="ld-empty-title">No interview loaded</div>
              <p className="ld-empty-sub">
                We could not find questions for a session in progress. Start or resume an interview and you will land
                back here.
              </p>
              <div className="row mt-2" style={{ gap: 10 }}>
                <Btn variant="mint" className="btn-pill" to="/interview-prep">
                  Go to Interview prep
                </Btn>
                <Btn variant="ghost" className="btn-pill" to="/aspire-quest">
                  Aspire Quest
                </Btn>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ============================ main render ============================ */
  return (
    <div className="csx">
      <div className="csx-page ie-page" onCopy={handleCopy} onPaste={handlePaste}>
        {/* ---------- sticky top bar ---------- */}
        <header className="ie-topbar">
          <div className="ie-topbar-in">
            <Link to={backTo} className="ie-back" onClick={LeaveInterview} title="Save draft and go back">
              <LuArrowLeft size={15} /> {backLabel}
            </Link>
            <div className="ie-progress">
              <span className="ie-count">
                Question {currentQuestionIndex + 1} of {total}
              </span>
              <div className="bar">
                <span style={{ width: `${pct}%` }} />
              </div>
            </div>
            <Btn variant="danger-soft" className="btn-pill" size="sm" onClick={() => setLeaveOpen(true)}>
              <LuLogOut size={14} /> Leave interview
            </Btn>
          </div>
        </header>

        <main className="ie-main">
          {/* ---------- question card ---------- */}
          <section className="ld-glass ie-question">
            <span className="ld-eyebrow">Question {currentQuestionIndex + 1}</span>
            <h1 className="ie-qtext">{questionText}</h1>
          </section>

          {isCode ? (
            /* ---------- coding question: Monaco + output ---------- */
            <section className="ie-code-grid">
              <div className="ie-editor">
                <div className="ie-editor-head">
                  <LuCode size={15} /> Code editor
                  {questionAnswer[currentQuestionIndex]?.language ? (
                    <span className="ie-lang">{questionAnswer[currentQuestionIndex]?.language}</span>
                  ) : null}
                </div>
                <div className="ie-monaco">
                  <MonacoEditor
                    width="100%"
                    height="240"
                    language={questionAnswer[currentQuestionIndex]?.language}
                    theme={monacoTheme}
                    value={answers[currentQuestionIndex] || ""}
                    onChange={handleEditorChange}
                  />
                </div>
                <div className="ie-editor-foot">
                  <Btn variant="ghost" className="btn-pill" size="sm" onClick={() => clearEditorAnswer()}>
                    <LuEraser size={14} /> Reset
                  </Btn>
                  <Btn variant="mint" className="btn-pill" size="sm" onClick={validate}>
                    <LuPlay size={14} /> Run code
                  </Btn>
                </div>
              </div>
              <div className="ie-editor">
                <div className="ie-editor-head">
                  <LuTerminal size={15} /> Output
                </div>
                <pre className="ie-output">{Output ? `${voutput.response}` : "Code output will appear here..."}</pre>
                <div className="ie-editor-foot">
                  <Btn variant="ghost" className="btn-pill" size="sm" onClick={() => setOutput(false)}>
                    Clear output
                  </Btn>
                </div>
              </div>
            </section>
          ) : (
            <>
              {/* ---------- text answer ---------- */}
              <section className="ie-answer">
                <label className="ie-label" htmlFor="answerTextarea">
                  Your answer
                </label>
                <textarea
                  id="answerTextarea"
                  className="input ie-textarea"
                  value={(answers[currentQuestionIndex] || "") + (voiceAssist ? transcript : "")}
                  onChange={handleAnswerChange}
                  placeholder="Type your answer here..."
                  disabled={voiceAssist}
                />
              </section>

              {/* ---------- speech recognition panel ---------- */}
              <section className="ld-glass ie-speech">
                <div className="ie-speech-head">
                  <span className="ie-label row" style={{ gap: 8 }}>
                    <LuMic size={15} style={{ color: "var(--accent)" }} /> Speech recognition
                  </span>
                  {browserSupportsSpeechRecognition ? (
                    voiceAssist ? (
                      <Btn variant="danger-soft" className="btn-pill" size="sm" onClick={toggleListening}>
                        <LuMicOff size={14} /> Stop recognition
                      </Btn>
                    ) : (
                      <Btn variant="mint" className="btn-pill" size="sm" onClick={toggleListening}>
                        <LuMic size={14} /> Start recognition
                      </Btn>
                    )
                  ) : (
                    <Btn variant="ghost" className="btn-pill" size="sm" disabled title="Voice input is not supported in this browser">
                      <LuMicOff size={14} /> Voice unavailable
                    </Btn>
                  )}
                </div>
                <div className={`ie-transcript${voiceAssist ? " live" : ""}`}>
                  {voiceAssist ? transcript || "Listening…" : "Speak to see transcription here..."}
                </div>
                <p className="t-xs mt-1">Tip: press the spacebar to start and stop recognition while not typing.</p>
              </section>
            </>
          )}

          {notification ? <Note tone="danger">{notification}</Note> : null}

          {/* ---------- bottom actions ---------- */}
          <div className="ie-actions">
            <Btn variant="ghost" className="btn-pill" onClick={SaveToDraft}>
              <LuSave size={15} /> Save draft
            </Btn>
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <Btn
                variant="ghost"
                className="btn-pill"
                onClick={handlePreviousQuestion}
                disabled={currentQuestionIndex === 0 || voiceAssist === true}
              >
                <LuChevronLeft size={15} /> Previous
              </Btn>
              {isLast ? (
                <Btn variant="mint" className="btn-pill" onClick={SubmitExam}>
                  <LuCircleCheck size={15} /> Submit interview
                </Btn>
              ) : (
                <Btn variant="mint" className="btn-pill" onClick={handleNextQuestion} disabled={voiceAssist === true}>
                  Next <LuChevronRight size={15} />
                </Btn>
              )}
            </div>
          </div>
        </main>

        {/* ---------- leave confirmation ---------- */}
        <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)}>
          <div className="t-h3 mb-1">Leave interview?</div>
          <p className="t-sm mb-3">
            Your progress will be saved as a draft — you can resume this interview later from {backLabel}.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
            <Btn variant="ghost" className="btn-pill" onClick={() => setLeaveOpen(false)}>
              Keep going
            </Btn>
            <Btn variant="danger-soft" className="btn-pill" onClick={confirmLeave}>
              <LuLogOut size={14} /> Save & leave
            </Btn>
          </div>
        </Dialog>

        {/* ---------- completion popup (same actions/targets as legacy) ---------- */}
        {showPopup ? (
          <div className="ie-scrim">
            <div className="ie-modal ie-done">
              <span className="ie-done-icon">
                <LuCircleCheck size={22} />
              </span>
              <h3 className="t-h3 mt-2">Your exam has been completed</h3>
              <p className="t-sm mt-1">Thank you for completing your exam. You can now return to the dashboard.</p>
              <div className="row mt-3" style={{ justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
                <Btn variant="mint" className="btn-pill" onClick={showResult}>
                  View result overview
                </Btn>
                <Btn variant="ghost" className="btn-pill" to={backTo} onClick={() => setShowPopup(false)}>
                  Back to {backLabel}
                </Btn>
              </div>
            </div>
          </div>
        ) : null}

        {/* ---------- results overview (data access identical to legacy) ---------- */}
        {examResultsPopup ? (
          <div className="ie-scrim">
            <div className="ie-modal">
              <div className="row-between mb-2">
                <div className="t-h3">Exam results overview</div>
                <Link
                  to={backTo}
                  aria-label="Close"
                  style={{ color: "var(--ink-3)" }}
                  onClick={() => setShowResultsPopup(false)}
                >
                  <LuX size={18} />
                </Link>
              </div>
              <hr className="divider mb-3" />
              <div className="stack-3">
                {resultQuestion.questions &&
                  resultQuestion.questions.map((item, index) => (
                    <div key={index} className="ie-qa">
                      <div className="row" style={{ alignItems: "flex-start", gap: 8 }}>
                        <LuCircleHelp size={15} style={{ color: "var(--info)", marginTop: 3, flex: "0 0 auto" }} />
                        <p className="ie-q">
                          Question {index + 1}: {item[index + 1] || "No Question"}
                        </p>
                      </div>
                      <div className="row mt-1" style={{ alignItems: "flex-start", gap: 8 }}>
                        <LuFileText size={15} style={{ color: "var(--ink-3)", marginTop: 3, flex: "0 0 auto" }} />
                        <p className="t-sm">Answer: {(resultQuestion.answers && resultQuestion.answers[index]) || "No Answer"}</p>
                      </div>
                      <div className="row mt-1" style={{ alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <LuStar size={15} style={{ color: "var(--amber)", flex: "0 0 auto" }} />
                        <span className="t-sm">Rating:</span>
                        <span className="row" style={{ gap: 2 }}>
                          {[...Array(10)].map((_, i) => {
                            let color = "#d1d5db";
                            const rating = resultQuestion.rating[index];
                            if (rating >= i + 1) {
                              if (rating <= 2) color = "#ef4444";
                              else if (rating <= 7) color = "#facc15";
                              else color = "#22c55e";
                            }
                            return <LuStar key={i} size={15} fill={color} style={{ color }} />;
                          })}
                        </span>
                      </div>
                      <div className="row mt-1" style={{ alignItems: "flex-start", gap: 8 }}>
                        <LuLightbulb size={15} style={{ color: "var(--accent-strong)", marginTop: 3, flex: "0 0 auto" }} />
                        <p className="t-sm">
                          Feedback:{" "}
                          {(resultQuestion.feedback_comments && resultQuestion.feedback_comments[index]) ||
                            "No Feedback"}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ) : null}

        {/* ---------- busy overlay ---------- */}
        {showLoader ? (
          <div className="ie-scrim">
            <div className="ie-busy">
              <Spinner lg />
              <div className="t-sm t-ink mt-2" style={{ fontWeight: 600 }}>
                Submitting your answers…
              </div>
              <div className="t-xs mt-1">This can take a moment — hang tight.</div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
